// src/main.tsx
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, useNavigate } from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";
import { deDE } from "@clerk/localizations"; // ← Deutsch
import App from "./App";
import "./index.css";
import { applyThemeCssVars } from "./theme";

applyThemeCssVars();

const pk = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string;

// Optional für PROD-Satellite (z. B. tools.propora.de als Satellite von accounts.domain)
const SATELLITE_ENABLED = import.meta.env.VITE_CLERK_IS_SATELLITE === "true";
const SATELLITE_DOMAIN = import.meta.env.VITE_CLERK_DOMAIN as string | undefined;

function AppWithRouter() {
  const navigate = useNavigate();

  const routerPush = (to: string) => navigate(to);
  const routerReplace = (to: string) => navigate(to, { replace: true });

  const isLocalhost =
    typeof window !== "undefined" && window.location.hostname === "localhost";

  // CI-Design zentral definieren
  // Helles Design (18.10.2026) – passend zu src/design/tokens.ts.
  const appearance = {
    variables: {
      colorPrimary: "#244737",
      colorText: "#173d30",
      colorTextSecondary: "#5e6d60",
      colorBackground: "#fffdf8",
      colorInputBackground: "#fffdf8",
      colorInputText: "#173d30",
      colorDanger: "#a14e37",
      borderRadius: "8px",
      spacingUnit: "8px",
      fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto',
    },
    elements: {
      userButtonPopoverCard: {
        background: "#fffdf8",
        border: "1px solid #d4d9ca",
        boxShadow: "0 16px 40px -16px rgba(23,61,48,0.35)",
        borderRadius: "12px",
      },
      userButtonPopoverActionButton: { color: "#173d30", borderRadius: "8px" },
      userButtonPopoverActionButton__danger: { color: "#a14e37" },
      userButtonPopoverActionButtonText: { color: "#173d30", fontSize: "13px" },
      userButtonPopoverActionButtonIcon: { color: "#5e6d60" },
      userPreviewMainIdentifier: { color: "#173d30", fontWeight: "600" },
      userPreviewSecondaryIdentifier: { color: "#5e6d60", fontSize: "12px" },
      userButtonPopoverFooter: { display: "none" },
      userButtonTrigger: { borderRadius: "50%" },
      userButtonAvatarBox: { width: "34px", height: "34px" },
      formButtonPrimary: { background: "#244737", color: "#fffdf8", fontWeight: "600" },
    },
  } as const;

  // Props für Clerk – inkl. Deutsch & CI
  const baseProps = {
    publishableKey: pk,
    routerPush,
    routerReplace,
    signInFallbackRedirectUrl: "/deals",
    signUpFallbackRedirectUrl: "/",
    afterSignOutUrl: "/login",
    localization: deDE,      // ← Deutsch aktiv
    appearance,              // ← CI-Styles
  } as const;

  // Lokal KEIN Satellite; in Prod optional, wenn per ENV aktiviert
  const providerProps =
    !isLocalhost && SATELLITE_ENABLED && SATELLITE_DOMAIN
      ? { ...baseProps, isSatellite: true as const, domain: SATELLITE_DOMAIN }
      : baseProps;

  return (
    <ClerkProvider {...providerProps}>
      <App />
    </ClerkProvider>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <BrowserRouter>
      <AppWithRouter />
    </BrowserRouter>
  </React.StrictMode>
);
